'use client';

import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Check, FileText, UserCog, Printer, Bell, BellOff, Ban, Trash2, Eye } from 'lucide-react';
import RemindersBadge from '@/components/RemindersBadge';

// Status badge styling
const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'bg-emerald-100 text-emerald-700';
    case 'declined':
    case 'deleted':
    case 'voided':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-sky-100 text-sky-700';
  }
};

// Get status text and color for recipient
const getRecipientStatusDisplay = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return { text: 'Completed', color: 'text-emerald-600' };
    case 'sent':
    case 'delivered':
      return { text: 'Awaiting', color: 'text-sky-600' };
    case 'autoresponded':
      // DocuSign hard bounce - the invite email was rejected (e.g. bad address).
      return { text: 'Failed to deliver', color: 'text-red-600' };
    case 'declined':
      return { text: 'Declined', color: 'text-red-600' };
    default:
      return { text: 'Pending', color: 'text-gray-400' };
  }
};

// Empty State Component
const EmptyState = () => (
  <div className="h-full flex flex-col items-center justify-center text-center p-8">
    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
      <FileText className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">No envelope selected</h3>
    <p className="text-sm text-gray-500 max-w-xs">
      Select an envelope from the list to view its details.
    </p>
  </div>
);

// Detailed Recipient Row (matches Classic view expanded state)
const RecipientRow = ({ recipient, canReassign, onReassign, onRemind, sendingReminderKey, envelopeId, formatDate, envelopeTimestamps }) => {
  const isCompleted = recipient.status === 'completed';
  const isAwaiting = ['sent', 'delivered'].includes(recipient.status?.toLowerCase());
  const isFailed = ['autoresponded', 'declined'].includes(recipient.status?.toLowerCase());
  const isPending = recipient.status === 'created';
  const statusDisplay = getRecipientStatusDisplay(recipient.status);

  // Display actual DocuSign routing order (null means parallel/no order)
  const displayOrder = recipient.routingOrder != null ? recipient.routingOrder : '—';

  // Build timestamp text like Classic view
  const getTimestampText = () => {
    if (isCompleted && recipient.timestamps?.signedDateTime) {
      return `Signed ${formatDate(recipient.timestamps.signedDateTime)}`;
    }
    if (recipient.status === 'delivered' && recipient.timestamps?.deliveredDateTime) {
      return `Received ${formatDate(recipient.timestamps.deliveredDateTime)}`;
    }
    if (isAwaiting) {
      // Use sent timestamp from envelope if recipient doesn't have one
      const sentTime = recipient.timestamps?.sentDateTime || recipient.reassignedAt || envelopeTimestamps?.sentDateTime || envelopeTimestamps?.createdDateTime;
      return sentTime ? `Requested ${formatDate(sentTime)}` : 'Awaiting signature';
    }
    if (isPending) {
      return 'Not yet sent';
    }
    if (isFailed) {
      return recipient.status?.toLowerCase() === 'declined'
        ? 'Declined by recipient'
        : 'Email could not be delivered - check the address';
    }
    return statusDisplay.text;
  };

  return (
    <div className="py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-start gap-3">
        {/* Routing order circle - shows actual DocuSign routing order */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium ${
            isCompleted ? 'bg-emerald-500 text-white' : isAwaiting ? 'bg-sky-500 text-white' : isFailed ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500'
          }`}
          title={`Routing order: ${recipient.routingOrder ?? 'None (parallel signing)'}`}
        >
          {isCompleted ? <Check className="w-4 h-4" /> : isFailed ? '!' : displayOrder}
        </div>

        {/* Name, email, timestamp */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {recipient.name || 'Unknown'}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {recipient.email}
          </p>
          <p className={`text-xs mt-0.5 ${isCompleted ? 'text-emerald-600' : isAwaiting ? 'text-sky-600' : isFailed ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
            {getTimestampText()}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex-shrink-0 flex items-center gap-1">
          {/* Remind button - show for sent/delivered recipients */}
          {isAwaiting && (
            <button
              onClick={() => onRemind(recipient)}
              disabled={sendingReminderKey === `${envelopeId}:${recipient.docuSignRecipientId}`}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors disabled:opacity-50"
              title="Send signing reminder email"
            >
              <Bell className="w-3 h-3" />
              {sendingReminderKey === `${envelopeId}:${recipient.docuSignRecipientId}` ? 'Sending...' : 'Remind'}
            </button>
          )}
          {/* Reassign button - allow for any non-completed recipient */}
          {canReassign && !isCompleted && (
            <button
              onClick={() => onReassign(recipient)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-sky-100 text-sky-700 rounded hover:bg-sky-200 transition-colors"
            >
              <UserCog className="w-3 h-3" />
              Reassign
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Document Row Component
const DocumentRow = ({ document, canRemove, onRemove, onView, isViewing }) => {
  return (
    <div className="py-2 border-b border-gray-100 last:border-b-0 flex items-center justify-between">
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm text-gray-900 truncate">{document.name}</p>
          <p className="text-xs text-gray-500">{document.pages} page{document.pages !== '1' ? 's' : ''}</p>
        </div>
      </div>
      <div className="flex-shrink-0 flex items-center gap-1">
        {/* Non-destructive read/print path. Deliberately always available - viewing
            never changes the envelope, and it must be easier to reach than Reassign. */}
        {onView && (
          <button
            onClick={onView}
            disabled={isViewing}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-sky-700 hover:bg-sky-50 rounded transition-colors disabled:opacity-50"
            title="Open this document to read or print it. Does not change the envelope."
          >
            <Eye className="w-3 h-3" />
            {isViewing ? 'Opening...' : 'View / Print'}
          </button>
        )}
        {canRemove && (
          <button
            onClick={onRemove}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Remove this document from the envelope"
          >
            <Trash2 className="w-3 h-3" />
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

const EnvelopeDetailPanel = ({
  envelope,
  userEmail,
  canReassign,
  signingInProgress,
  signedEnvelopeId,
  processingEnvelopeId,
  handleSigningRequest,
  openReassignModal,
  handleSendReminder,
  sendingReminderKey,
  openVoidModal,
  formatDate,
  hasSequentialSigning,
  documents,
  isLoadingDocuments,
  onFetchDocuments,
  onRemoveDocument,
  onViewDocument,
  viewingDocId,
  onSetReminders,
  remindersUpdatingId
}) => {
  const remindersUpdating = remindersUpdatingId === envelope?.envelopeId;
  // Fetch documents when envelope changes
  useEffect(() => {
    if (envelope?.envelopeId && onFetchDocuments) {
      onFetchDocuments(envelope.envelopeId);
    }
  }, [envelope?.envelopeId, onFetchDocuments]);

  if (!envelope) {
    return <EmptyState />;
  }

  const isTerminalState = ['declined', 'deleted', 'voided'].includes(envelope.status?.toLowerCase());
  const isVoidable = !isTerminalState && envelope.status?.toLowerCase() !== 'completed';
  const completedCount = envelope.recipients?.filter(r => r.status === 'completed').length || 0;
  const totalCount = envelope.recipients?.length || 0;

  // Check if current user needs to sign
  const needsUserSignature = () => {
    const isActive = ['sent', 'delivered'].includes(envelope.status?.toLowerCase());
    const isNotDeleted = !isTerminalState;
    const needsSignature = envelope.recipients?.some(recipient => {
      const emailMatch = recipient.email?.toLowerCase() === userEmail?.toLowerCase();
      const statusMatch = ['sent', 'delivered'].includes(recipient.status?.toLowerCase());
      return emailMatch && statusMatch;
    });
    return isActive && isNotDeleted && needsSignature;
  };

  const handleSign = (e) => {
    const recipient = envelope.recipients.find(r =>
      r.email?.toLowerCase() === userEmail?.toLowerCase() &&
      ['sent', 'delivered'].includes(r.status?.toLowerCase())
    );
    handleSigningRequest(envelope.envelopeId, e, recipient?.routingOrder || null);
  };

  const handleReassign = (recipient) => {
    openReassignModal(envelope, recipient);
  };

  const handleRemind = (recipient) => {
    handleSendReminder(envelope.envelopeId, recipient.docuSignRecipientId, recipient.name);
  };

  // Sort recipients by routing order
  const sortedRecipients = [...(envelope.recipients || [])].sort(
    (a, b) => (a.routingOrder ?? 999) - (b.routingOrder ?? 999)
  );

  // Build date string
  const buildDateString = () => {
    const parts = [];
    if (envelope.timestamps?.createdDateTime) {
      parts.push(`Created ${formatDate(envelope.timestamps.createdDateTime)}`);
    }
    if (envelope.timestamps?.sentDateTime) {
      parts.push(`Sent ${formatDate(envelope.timestamps.sentDateTime)}`);
    }
    if (envelope.timestamps?.completedDateTime) {
      parts.push(`Completed ${formatDate(envelope.timestamps.completedDateTime)}`);
    }
    return parts.join(' • ');
  };

  // Print envelope details
  const handlePrint = () => {
    const printContent = `
      <html>
        <head>
          <title>${envelope.subject || 'Envelope Details'}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; color: #333; }
            h1 { font-size: 18px; margin-bottom: 8px; }
            .status { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 500; }
            .status-completed { background: #d1fae5; color: #065f46; }
            .status-pending { background: #e0f2fe; color: #0369a1; }
            .meta { color: #6b7280; font-size: 12px; margin: 8px 0 16px; }
            .section { margin-top: 16px; }
            .section-title { font-size: 14px; font-weight: 600; margin-bottom: 8px; }
            .recipient { display: flex; align-items: center; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
            .recipient:last-child { border-bottom: none; }
            .recipient-num { width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; margin-right: 12px; }
            .completed { background: #10b981; color: white; }
            .awaiting { background: #0ea5e9; color: white; }
            .pending { background: #e5e7eb; color: #6b7280; }
            .failed { background: #ef4444; color: white; }
            .recipient-name { flex: 1; }
            .recipient-status { font-size: 12px; }
            .envelope-id { margin-top: 20px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>${envelope.subject || 'Untitled Document'}</h1>
          <div class="meta">
            <span class="status ${envelope.status?.toLowerCase() === 'completed' ? 'status-completed' : 'status-pending'}">${envelope.status}</span>
            &nbsp;&nbsp;${completedCount}/${totalCount} signed &nbsp;•&nbsp; ${buildDateString()}
          </div>
          <div class="section">
            <div class="section-title">Recipients</div>
            ${sortedRecipients.map((r) => {
              const isComp = r.status === 'completed';
              const isAwait = ['sent', 'delivered'].includes(r.status?.toLowerCase());
              const isFail = ['autoresponded', 'declined'].includes(r.status?.toLowerCase());
              const displayOrder = r.routingOrder != null ? r.routingOrder : '—';
              return `
                <div class="recipient">
                  <div class="recipient-num ${isComp ? 'completed' : isAwait ? 'awaiting' : isFail ? 'failed' : 'pending'}">${isComp ? '✓' : isFail ? '!' : displayOrder}</div>
                  <div class="recipient-name">${r.name || r.email}</div>
                  <div class="recipient-status">${isComp ? 'Signed' + (r.timestamps?.signedDateTime ? ' ' + formatDate(r.timestamps.signedDateTime) : '') : isAwait ? 'Awaiting' : isFail ? getRecipientStatusDisplay(r.status).text : 'Pending'}</div>
                </div>
              `;
            }).join('')}
          </div>
          <div class="envelope-id">Envelope ID: ${envelope.envelopeId}</div>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4">
        {/* Header - Title + Print */}
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg font-semibold text-gray-900 leading-tight">
            {envelope.subject || 'Untitled Document'}
          </h2>
          <div className="flex items-center gap-1">
            {/* Reminder toggle sits BEFORE Void deliberately. Staff arrive here wanting
                the emails to stop, and Void is what they reach for when nothing else
                exists - the same pattern that caused the consent incidents. The
                non-destructive option should be the one they meet first. */}
            {!isTerminalState && onSetReminders && (
              <button
                onClick={() => onSetReminders(envelope.envelopeId, envelope.remindersEnabled === false)}
                disabled={remindersUpdating}
                className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors disabled:opacity-50 ${
                  envelope.remindersEnabled === false
                    ? 'text-slate-700 hover:bg-slate-100'
                    : 'text-sky-700 hover:bg-sky-50'
                }`}
                title={envelope.remindersEnabled === false
                  ? 'Resume automatic reminder emails for this envelope'
                  : 'Stop automatic reminder emails. Does not void the envelope - it stays open and can still be signed.'}
              >
                {envelope.remindersEnabled === false
                  ? <><Bell className="w-3.5 h-3.5" />{remindersUpdating ? 'Working...' : 'Resume reminders'}</>
                  : <><BellOff className="w-3.5 h-3.5" />{remindersUpdating ? 'Working...' : 'Stop reminders'}</>}
              </button>
            )}
            {isVoidable && (
              <button
                onClick={() => openVoidModal(envelope)}
                className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Void this envelope"
              >
                <Ban className="w-3.5 h-3.5" />
                Void
              </button>
            )}
            <button
              onClick={handlePrint}
              className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title="Print envelope details"
            >
              <Printer className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Subtitle - Status + count + dates */}
        <div className="mt-1 flex items-center gap-2 flex-wrap text-sm">
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${getStatusBadge(envelope.status)}`}>
            {envelope.status}
          </span>
          <span className="text-gray-500">{completedCount}/{totalCount} signed</span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-500 text-xs">{buildDateString()}</span>
          <RemindersBadge remindersEnabled={envelope.remindersEnabled} />
        </div>

        {/* Recipients Section */}
        {!isTerminalState && sortedRecipients.length > 0 && (
          <div className="mt-4">
            {/* Recipients header with Sign Now button */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">Recipients</h3>
              <div className="flex items-center gap-2">
                {signedEnvelopeId === envelope.envelopeId && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                    <Check className="w-3 h-3" /> Signed
                  </span>
                )}
                {needsUserSignature() && (
                  <Button
                    onClick={handleSign}
                    className="bg-[#2db3e1] text-white hover:bg-[#25a0cc] px-3 py-1 h-auto text-xs rounded font-medium"
                    disabled={signingInProgress || signedEnvelopeId === envelope.envelopeId || processingEnvelopeId === envelope.envelopeId}
                  >
                    {signingInProgress || processingEnvelopeId === envelope.envelopeId ? 'Signing...' : 'Sign Now'}
                  </Button>
                )}
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-3">
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-300"
                  style={{ width: `${(completedCount / totalCount) * 100}%` }}
                />
              </div>
            </div>

            {/* Recipient list */}
            <div className="bg-gray-50 rounded-lg px-3 py-1">
              {sortedRecipients.map((recipient) => (
                <RecipientRow
                  key={`${recipient.email}-${recipient.routingOrder}`}
                  recipient={recipient}
                  canReassign={canReassign}
                  onReassign={handleReassign}
                  onRemind={handleRemind}
                  sendingReminderKey={sendingReminderKey}
                  envelopeId={envelope.envelopeId}
                  formatDate={formatDate}
                  envelopeTimestamps={envelope.timestamps}
                />
              ))}
            </div>
          </div>
        )}

        {/* Documents Section */}
        {documents && documents.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Documents</h3>
            <div className="bg-gray-50 rounded-lg px-3 py-1">
              {documents.map((doc) => (
                <DocumentRow
                  key={doc.documentId}
                  document={doc}
                  canRemove={!isTerminalState && envelope.status?.toLowerCase() !== 'completed' && documents.length > 1 && !envelope.recipients?.some(r => r.status === 'completed')}
                  onRemove={() => onRemoveDocument(envelope.envelopeId, doc)}
                  onView={onViewDocument ? () => onViewDocument(envelope.envelopeId, doc.documentId) : undefined}
                  isViewing={viewingDocId === doc.documentId}
                />
              ))}
            </div>
          </div>
        )}
        {isLoadingDocuments && !documents && (
          <div className="mt-4">
            <p className="text-xs text-gray-400">Loading documents...</p>
          </div>
        )}

        {/* Terminal State Message */}
        {isTerminalState && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">
              This envelope has been {envelope.status?.toLowerCase()}. No further actions can be taken.
            </p>
          </div>
        )}

        {/* Footer - Timestamps and Envelope ID (matches Classic view) */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            {envelope.timestamps?.createdDateTime && (
              <span>Created: {formatDate(envelope.timestamps.createdDateTime)}</span>
            )}
            {envelope.timestamps?.lastModifiedDateTime && (
              <span>Updated: {formatDate(envelope.timestamps.lastModifiedDateTime)}</span>
            )}
            {envelope.timestamps?.expireDateTime && (
              <span>Expires: {formatDate(envelope.timestamps.expireDateTime)}</span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Envelope ID: {envelope.envelopeId}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EnvelopeDetailPanel;
